import type { CampaignState } from '@/hooks/useRouterQuery';
import { useId } from 'react';

import type { InputProps } from '@poseidon/components/Input2';
import Button from '@poseidon/components/Input2/Button';
import FilterPopover from '@poseidon/components/Input2/FilterPopover';
import { Alerts, Communication, General, Media } from '@poseidon/components/StrokeIcons';
import { DEFAULT_SIGNAL_BUCKETS } from '@poseidon/shared-utils/signalDefaults';

import { FilterStageIcon } from './FilterStageIcon';

export const STAGE_ALL_OPTION_VALUE = '__ALL_STAGES__';

type StageOptionShared = { id: string; name: string; color?: string | null; order?: number | null };

type StageMultiFilterProps = {
  size?: InputProps['size'];
  isPending?: boolean;
  disabled?: boolean;
  value: string[];
  onChange: (next: string[]) => void;
  stages: StageOptionShared[];
};

/**
 * Multi-select stage filter. Mirrors `StageFilter` but lets the user pick
 * any combination of stages — the underlying SQL is an `IN (...)` clause, so
 * picks are OR'd within this filter and AND'd against the other filters
 * surrounding it.
 */
export function StageMultiFilter({ isPending, size, value, onChange, disabled, stages }: StageMultiFilterProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const stageOptions = stages.map((stage) => ({
    value: stage.id,
    label: stage.name,
    icon: <FilterStageIcon color={stage.color} order={stage.order} />,
  }));

  const selectedStages = stages.filter((s) => value.includes(s.id));
  const singleSelected = selectedStages.length === 1 ? selectedStages[0] : undefined;
  const label = singleSelected
    ? singleSelected.name
    : selectedStages.length === 0
      ? 'All stages'
      : `${selectedStages.length} stages`;

  const triggerIcon = singleSelected ? (
    <FilterStageIcon color={singleSelected.color} order={singleSelected.order} />
  ) : undefined;

  const toggle = (id: string) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        size={size}
        disabled={isPending || disabled}
        icon={triggerIcon}>
        {isPending ? 'Loading...' : label}
      </Button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        items={stageOptions}
        multiSelectedValues={value}
        onSelect={toggle}
        filterable={stages.length > 5}
      />
    </>
  );
}

type StageOption = { id: string; name: string; color?: string | null; order?: number | null };

type StageFilterProps = {
  size?: InputProps['size'];
  isPending?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (option: { value: string; label: string }) => void;
  stages: StageOption[];
};

export function StageFilter({ isPending, size, value, onChange, disabled, stages }: StageFilterProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const stageOptions = stages.map((stage) => ({
    value: stage.id,
    label: stage.name,
    icon: <FilterStageIcon color={stage.color} order={stage.order} />,
  }));

  const options = [{ value: STAGE_ALL_OPTION_VALUE, label: 'All stages', icon: undefined }, ...stageOptions];

  const selectedValue = value ?? STAGE_ALL_OPTION_VALUE;
  const selectedOption = options.find((option) => option.value === selectedValue);

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        size={size}
        disabled={isPending || disabled}
        icon={selectedOption?.value === STAGE_ALL_OPTION_VALUE ? undefined : selectedOption?.icon}>
        {isPending ? 'Loading...' : selectedOption?.label}
      </Button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        items={options}
        selectedValue={selectedValue}
        onSelect={(val) => {
          const opt = options.find((o) => o.value === val);
          if (opt) onChange?.(opt);
        }}
        filterable={false}
      />
    </>
  );
}

type SignalMultiFilterProps = {
  size?: InputProps['size'];
  disabled?: boolean;
  value: string[];
  onChange: (next: string[]) => void;
};

/**
 * Multi-select signal-bucket filter. Picks within the filter OR together
 * (server-side `EXISTS` over `contact_signal_aspect.bucket_key IN (...)`);
 * the filter still AND's against every other filter on the page.
 */
export function SignalMultiFilter({ size, value, onChange, disabled }: SignalMultiFilterProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const bucketOptions = DEFAULT_SIGNAL_BUCKETS.map((b) => ({
    value: b.key,
    label: b.name,
    icon: <Communication.Rss01 className="size-4 text-mantis-500" />,
  }));

  const selectedBuckets = DEFAULT_SIGNAL_BUCKETS.filter((b) => value.includes(b.key));
  const singleSelected = selectedBuckets.length === 1 ? selectedBuckets[0] : undefined;
  const label = singleSelected
    ? singleSelected.name
    : selectedBuckets.length === 0
      ? 'Any signal'
      : `${selectedBuckets.length} signals`;

  const toggle = (key: string) => {
    if (value.includes(key)) onChange(value.filter((v) => v !== key));
    else onChange([...value, key]);
  };

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        size={size}
        disabled={disabled}>
        {label}
      </Button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        items={bucketOptions}
        multiSelectedValues={value}
        onSelect={toggle}
        filterable={bucketOptions.length > 5}
      />
    </>
  );
}

// Campaign-state options for the multi-select filter. Restricted to states that
// a live `cadence.state` can hold (so the server's `state IN (...)` matches) —
// this is a subset of the full CadenceState enum, excluding `not-started` and
// `deleted`. Labels/icons mirror the single-select `StatesFilterSelect`.
const CAMPAIGN_STATE_OPTIONS = [
  { value: 'active', label: 'Active', icon: <General.Zap /> },
  { value: 'paused', label: 'Paused', icon: <Media.PauseCircle /> },
  { value: 'action-required', label: 'Needs Action', icon: <Alerts.Bell01 /> },
  { value: 'responded', label: 'Replied', icon: <Communication.MessageSquare01 /> },
  { value: 'interrupted', label: 'Interrupted', icon: <Alerts.AlertCircle /> },
  { value: 'finished', label: 'Finished', icon: <General.CheckCircle /> },
  { value: 'pending-review', label: 'Pending Review', icon: <General.Eye /> },
] satisfies { value: CampaignState; label: string; icon: React.ReactNode }[];

type StatesMultiFilterProps = {
  size?: InputProps['size'];
  disabled?: boolean;
  value: CampaignState[];
  onChange: (next: CampaignState[]) => void;
};

/**
 * Multi-select campaign-state filter. Mirrors `StageMultiFilter`: picks OR
 * together (server-side `cadence.state IN (...)` over non-deleted cadences) and
 * the filter AND's against every other filter on the page.
 */
export function StatesMultiFilter({ size, value, onChange, disabled }: StatesMultiFilterProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const selected = CAMPAIGN_STATE_OPTIONS.filter((o) => value.includes(o.value));
  const singleSelected = selected.length === 1 ? selected[0] : undefined;
  const label = singleSelected
    ? singleSelected.label
    : selected.length === 0
      ? 'Any state'
      : `${selected.length} states`;

  // FilterPopover emits the raw option string; resolve it back to a typed
  // CampaignState via the options list so we never widen `value` to string[].
  const toggle = (val: string) => {
    const match = CAMPAIGN_STATE_OPTIONS.find((o) => o.value === val);
    if (!match) return;
    if (value.includes(match.value)) onChange(value.filter((v) => v !== match.value));
    else onChange([...value, match.value]);
  };

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        size={size}
        disabled={disabled}
        icon={singleSelected?.icon}>
        {label}
      </Button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        items={[...CAMPAIGN_STATE_OPTIONS]}
        multiSelectedValues={value}
        onSelect={toggle}
        filterable={false}
      />
    </>
  );
}
